import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { getProjectHubContextKey, loadProjectHub, resetProjectHubState } from "./projectHubLoadState";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { subcontractReturnUrl, subcontractUrl } from "../lib/subcontractWorkspace";
import SubcontractorSchedule from "../components/SubcontractorSchedule";
import { HandoffReceipt } from "../components/HandoffReceipt";
import DesignChanges from "../components/DesignRevisionTasks";
import { RfqComparePanel } from "../components/RfqComparePanel";
import { SubRatingPanel } from "../components/SubRatingPanel";
import EditProject from "../components/EditProject";
import ProjectManagementActions from "../components/ProjectManagementActions";
import { canEditProject } from "../lib/teamScope";
// 二-12：工期变更板块已隐藏（见下方 DateChangeCard 调用处注释），导入暂时停用，恢复时一并启用。
// import { DateChangeCard } from "../components/DateChangeCard";
import { useStaffOptions } from "../components/StaffPicker";
import ContactDrop from "../components/hub/ProjectContacts";
import { HubDiscussion } from "../components/HubDiscussion";
import { ProjectDiscussionTab } from "../components/chat/ProjectDiscussionTab";
import { ReturnToDesignDialog } from "../components/ReturnToDesignDialog";

/* 「讨论」页签：项目笔记 + 聊天归档 + 待办 + 协作任务 + ✦ Muse 日报 合并时间线（默认折叠；?section=discussion 展开） */
function DiscussionCard({ api, id, zh, user, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  useEffect(() => { if (defaultOpen) { setOpen(true); setTimeout(() => document.getElementById("hub-discussion-card")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200); } }, [defaultOpen]);
  return (
    <div id="hub-discussion-card" className="rounded-2xl border border-slate-200/80 bg-white shadow-sm" data-testid="hub-discussion-card" data-open={open ? 1 : 0}>
      <button type="button" data-testid="hub-discussion-toggle" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-bold text-slate-900">{zh ? "讨论 · 聊天归档 / 待办 / 协作 / ✦ Muse 日报" : "Discussion · chat archive / todos / collab / ✦ Muse"}</span>
        <span className="text-[11px] text-[#4F6BFF] font-semibold">{open ? (zh ? "收起" : "Collapse") : (zh ? "展开全部" : "Expand all")}</span>
      </button>
      {/* 二-11：收起态下显示最近 3 条讨论预览，而非完全空白；展开后才加载完整列表（含统计卡、筛选栏、回到原话等）。
          老板原话 32:52-33:43：讨论不用全部展开，显示最近两三条即可，翻聊天记录太麻烦。 */}
      {open
        ? <div className="px-4 pb-4"><ProjectDiscussionTab api={api} id={id} zh={zh} user={user} /></div>
        : <div className="px-4 pb-3"><ProjectDiscussionTab api={api} id={id} zh={zh} user={user} previewLimit={3} /></div>}
    </div>
  );
}
import { TraceWorkspace } from "../components/TraceWorkspace";
import { FloatWindow } from "../components/FloatWindow";
import ProjectFiles from "../components/ProjectFiles";
// 施工日志（Daily Logs）：原功能活在 ProjectDialog 的「施工日志」标签里，但工程侧现在只剩
// 「分包商管理」入口（subsOnly 模式会隐藏该标签）→ 全站没有入口能打开日志。这里直接引组件补回入口。
import ProjectDailyLogs from "../components/ProjectDailyLogs";
import { fmtD } from "../lib/date";
import {
  ArrowLeft, Bot, CalendarDays, Camera, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Download, FilePlus2, FileUp, FolderOpen, Image as ImageIcon, LayoutGrid, List, Loader2,
  MapPin, Maximize2, Megaphone, MailCheck, Paperclip, Pencil, Plus, RefreshCw, Rocket, RotateCcw, Send, Star, Upload, User, UsersRound,
} from "lucide-react";
import confetti from "canvas-confetti";
import { chunkUpload } from "../components/EnginePanel";
import { SitePhotosCard } from "../components/hub/SitePhotosCard";
import { EvidenceLightbox } from "../components/EvidenceLightbox";
import { CompleteNoteModal } from "../components/CompleteNoteModal";
import { CommRecapDialog } from "../components/CommRecapDialog";
import AddressMapModal, { gmapsSearchUrl } from "../components/hub/AddressMapModal";
import VrLinkButton from "../components/hub/VrLinkButton";
import WeeklyInspectionSummary from "../components/hub/WeeklyInspectionSummary";

import { T } from "../lib/i18n";
import { CardSkeleton } from "../components/States";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROLE_COLOR = {
  crew: "bg-orange-100 text-orange-700", owner: "bg-blue-100 text-blue-700", cdc: "bg-violet-100 text-violet-700",
  sub: "bg-teal-100 text-teal-700", vendor: "bg-pink-100 text-pink-700", pm: "bg-slate-200 text-slate-700",
  city: "bg-red-100 text-red-700", sales: "bg-emerald-100 text-emerald-700", system: "bg-slate-100 text-slate-500",
};
const todayStr = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

/**
 * ProjectHub —— 项目详情（工程 Hub）
 *
 * 两种形态（2026-10-15 抽屉化改造，参考已签约客户详情）：
 *   · 整页态：路由 /project/:id 直接渲染（兜底 / 直链改写前的短暂态）；
 *   · embedded：由 GlobalProjectDrawer 挂在 CrmDetailDrawer 里渲染，
 *     id 走 projectId prop、返回/删后跳转走 onClose（关抽屉回来源页）。
 *     深链参数（?task= / ?sub= / ?section= / ?rate= / ?ms=）继续从 useLocation().search 读 ——
 *     抽屉态下它们就在来源页的 query 上（GlobalProjectDrawer 的 URL 契约），无需适配。
 * 分包商管理属于项目级入口，与全流程施工计划并排放在工作台上方，避免混入任务操作栏。
 * 分包入口与历史深链转到独立工作页；保留 hub-files-btn 标识，兼容已有入口定位。
 * 分包商施工日程改为紧邻分包商管理的按钮并通过弹窗打开，避免独立折叠条占据页面。
 * 复用原日程组件、角色/菜单权限及项目范围；Radix 负责关闭与焦点返回，原日程深链继续有效。
 * 按项目详情精简需求移除留言板和五项 KPI 展示，现场快照独占一行，避免留下半行空白。
 * 仅移除展示及留言操作入口；历史留言、统计接口和简报仍使用的 KPI 数据保持兼容。
 * 顶部「现场拍照」展开并定位到现有现场快照区，避免上传入口藏在长任务列表下方而看似失效。
 */
export default function ProjectHub({ embedded = false, projectId: propId, onClose } = {}) {
  const { id: routeId } = useParams();
  const id = propId || routeId;
  const nav = useNavigate();
  const rateFocus = new URLSearchParams(useLocation().search).get("rate") || "";
  const focusSub = new URLSearchParams(useLocation().search).get("sub") || "";
  const focusSection = new URLSearchParams(useLocation().search).get("section") || "";
  const focusTask = new URLSearchParams(useLocation().search).get("task") || "";
  const { api, user, lang, permOf, permMap } = useApp();
  const zh = lang === "zh";
  const kickoffManagePerm = permOf("/board::kickoff-manage");
  const hubContextKey = getProjectHubContextKey({ id, user, lang, permMap });
  const [data, setData] = useState(null);
  const hubContextRef = useRef({ key: hubContextKey, generation: 0 });
  const hubLoadSequenceRef = useRef(0);
  const hubTerminalRef = useRef({ generation: -1, sequence: -1 });
  const [err, setErr] = useState("");
  const [errStatus, setErrStatus] = useState(null);
  const [loadBusy, setLoadBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const location = useLocation();
  // 日程从底部折叠区迁到项目操作区；弹窗打开时才加载同一批派工记录，旧深链仍直接打开本项目月历。
  const [showSchedule, setShowSchedule] = useState(focusSection === "schedule");
  useEffect(() => { if (focusSection === "schedule") setShowSchedule(true); }, [focusSection]);
  // 历史通知经全局项目抽屉进入时，保留节点/验收参数并跳到独立工作页。
  // 返回源地址去掉深链，防止“返回项目”立即再次重定向。
  const subcontractReturn = subcontractReturnUrl(location.pathname, location.search, id);
  // 日程里的询价、建合同和线下补录都跳转到同一分包工作台，避免另造合同数据入口。
  const openSubcontractCreate = (mode) => {
    const target = new URL(subcontractUrl(id), window.location.origin);
    target.searchParams.set("new", mode);
    setShowSchedule(false);
    nav(`${target.pathname}${target.search}`, { state: { subcontractReturn } });
  };
  useEffect(() => {
    if (!focusSub || /\/subcontracts$/.test(location.pathname)) return;
    const q = new URLSearchParams(location.search); q.delete("project");
    nav(`/project/${encodeURIComponent(id)}/subcontracts?${q}`, { replace: true, state: { subcontractReturn } });
  }, [focusSub, id, location.pathname, location.search, nav, subcontractReturn]);
  const [showFiles, setShowFiles] = useState(false);
  // 【2026-10-21 修复】「现场快照」卡右上角的「文件归档 · 现场记录 N」曾直接复用 legacy 状态
  // （onArchive={() => setLegacy(true)}），而 legacy 打开的是「分包商管理」弹窗 —— 用户点「文件归档」
  // 看到的却是分包商界面，与按钮文案完全不符。
  // 现在把「打开项目文件」和「定位到哪个分类」拆成两个状态：头部「项目文件」按钮不带分类，
  // 现场快照的归档入口带上 job_site_photos（现场快照落库时就是这个分类，见 project_hub.hub_photo_add），
  // 打开后直接滚到「工地现场照片」分组，避免用户还要在 10 个分类里自己找。
  const [filesFocus, setFilesFocus] = useState("");
  const openFiles = (cat = "") => { setFilesFocus(cat); setShowFiles(true); };
  const [showNewTask, setShowNewTask] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [briefText, setBriefText] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const photosSectionRef = useRef(null);
  const hubHeaderRef = useRef(null);
  /**
   * 打开现场上传入口（无参数、无返回值）：原来仅修改 showUpload，面板在屏外展开，
   * 用户点击顶部按钮看不到反馈。定位常驻的现场快照区，其顶部不受上传面板展开影响；
   * 同时把键盘焦点带到该区，后续 Tab 可继续操作。每次点击都定位，已展开时也有效。
   * scrollIntoView 同时兼容整页和抽屉的滚动容器；立即定位避免长距离动画及减少动态效果偏好问题。
   * 页头会吸顶且在窄屏换行，因此按当次实际高度及吸顶偏移留位，防止上传按钮被页头遮住。
   */
  const openPhotoUpload = () => {
    setShowUpload(true);
    const section = photosSectionRef.current;
    const header = hubHeaderRef.current;
    if (!section) return;
    const headerOffset = header ? header.getBoundingClientRect().height + (parseFloat(window.getComputedStyle(header).top) || 0) : 0;
    section.style.scrollMarginTop = `${headerOffset + 16}px`;
    section.focus({ preventScroll: true });
    section.scrollIntoView({ behavior: "instant", block: "start" });
  };
  const [showRecap, setShowRecap] = useState(false);
  const [tick, setTick] = useState(0);
  // 退回设计部：预检结果（服务端判定能否退回）+ 弹窗开关。仅 admin / ceo / eng_manager 拉取。
  const [retCheck, setRetCheck] = useState(null);
  const [retOpen, setRetOpen] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  // Do not leave another project's or account's details visible while the new context loads.
  useLayoutEffect(() => {
    const priorHubContext = hubContextRef.current;
    if (priorHubContext.key === hubContextKey) return;
    hubContextRef.current = { key: hubContextKey, generation: priorHubContext.generation + 1 };
    resetProjectHubState({ setData, setErr, setErrStatus, setLoadBusy, setAiBusy });
  }, [hubContextKey]);

  // 手机网络中断不代表项目已删除：只在接口明确返回 404 时显示不存在，其余失败允许原地重试。
  // 详情请求可能因重挂载或上下文刷新而重叠；较晚结束的取消不能覆盖已完成结果，旧项目/账号/范围的响应也必须丢弃。
  const load = useCallback(() => loadProjectHub({
    api, id, zh, contextRef: hubContextRef, sequenceRef: hubLoadSequenceRef, terminalRef: hubTerminalRef,
    setData, setErr, setErrStatus, setLoadBusy,
  }), [api, id, zh]);
  const bump = useCallback(() => { load(); setTick((t) => t + 1); }, [load]);

  const sendOwnerSurvey = async () => {
    if (!window.confirm(zh ? "向业主发送满意度评分链接短信？（已提交过评价的不会重复发送）" : "Send satisfaction survey link to the owner?")) return;
    try {
      const { data } = await api.post(`/projects/${id}/send-owner-survey`);
      if (data.submitted) { toast.info(zh ? "业主已提交过评价" : "Owner already submitted"); return; }
      try { await navigator.clipboard.writeText(data.link); } catch {}
      toast.success((zh ? "评价链接已复制" : "Survey link copied") + (data.sms ? (zh ? "，短信已发送" : ", SMS sent") : (zh ? "（无电话/短信未发出，可手动转发链接）" : " (no SMS sent)")));
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
  };

  useEffect(() => {
    const generation = hubContextRef.current.generation;
    load().then((d) => {
      if (hubContextRef.current.generation !== generation) return;
      if (d && (!d.aiDiag || d.aiDiagDay !== todayStr())) {
        setAiBusy(true);
        api.post(`/projects/${id}/hub/ai-diagnose`, {})
          .then(({ data: r }) => {
            if (hubContextRef.current.generation === generation) setData((prev) => prev ? { ...prev, aiDiag: r.result, aiDiagDay: todayStr() } : prev);
          })
          .catch(() => {})
          .finally(() => { if (hubContextRef.current.generation === generation) setAiBusy(false); });
      }
    });
  }, [load, api, id, hubContextKey]);

  // 退回设计部预检：按钮要不要显示、点开弹窗显示什么判据，全部由服务端 return-check 说了算，
  // 前端不自行推断 —— 避免「工程侧看着像没开工、其实已进场」的误退。
  useEffect(() => {
    // 【第七批】开工管理动作（退回设计部预检）→ /board::kickoff-manage（与后端 kickoff.MGR_ROLES 同一判定）
    if (kickoffManagePerm === undefined ? !["admin", "ceo", "eng_manager"].includes(user?.role) : !kickoffManagePerm) { setRetCheck(null); return; }
    let alive = true;
    api.get(`/kickoff/${id}/return-check`)
      .then(({ data }) => { if (alive) setRetCheck(data); })
      .catch(() => { if (alive) setRetCheck(null); });
    return () => { alive = false; };
  }, [api, id, kickoffManagePerm, user?.role, tick]);

  if (err) return (
    <div className="text-center" data-testid="hub-error" aria-busy={loadBusy}>
      <p className="text-slate-700 text-sm font-semibold">{errStatus === 404 ? (zh ? "该项目不存在或已被删除" : "Project not found") : (zh ? "项目资料加载失败" : "Unable to load project details")}</p>
      <p className="text-slate-500 text-sm mt-1" role="status">{err}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        {errStatus !== 404 && <Button data-testid="hub-error-retry" disabled={loadBusy} className="min-h-11" onClick={load}>{loadBusy ? (zh ? "正在重试…" : "Retrying…") : (zh ? "重试" : "Retry")}</Button>}
        <Button data-testid="hub-error-back" size="sm" variant="outline" className="min-h-11" onClick={() => (embedded && onClose ? onClose() : window.history.back())}><ArrowLeft className="h-3.5 w-3.5 mr-1" />{zh ? "返回上一页" : "Go back"}</Button>
      </div>
    </div>
  );
  if (!data) return <div className="space-y-4" data-testid="hub-loading"><CardSkeleton count={1} className="grid-cols-1" /><CardSkeleton count={3} className="grid-cols-1 md:grid-cols-3" /></div>;
  const p = data.project;
  const buildBrief = () => {
    const tm = data.kpi.timing;
    const ns = data.nextStep || {};
    return T("【工程简报】{0}\n阶段: {1} (滞留{2}天/标准{3}天)\n进度: {4}% (目标{5}%)\n引擎判定: {6} · {7}\n阻塞任务: {8} 项 · 今日必办: {9} 项\n业主: {10} · 团队: {11} · 经理: {12}", [p.address, tm ? T(tm.phaseName) : "-", tm ? tm.stayed : 0, tm ? tm.standard : 0, data.kpi.progress, data.kpi.target, ns.status || "-", (zh ? (ns.title || ns.label) : (ns.titleEn || ns.labelEn || T(ns.title || ns.label))) || "-", data.kpi.stuckCount, data.kpi.todayCount, p.clientName || "-", p.teamName || p.team || "-", p.manager || "-"]);
  };

  return (
    <div className="space-y-4" data-testid="hub-page">
      <HubHeader headerRef={hubHeaderRef} p={p} zh={zh} nav={nav} api={api} id={id} user={user} embedded={embedded} onClose={onClose}
        onFiles={() => openFiles()} onEdit={() => setEditOpen(true)}
        onPhoto={openPhotoUpload} onBrief={() => setBriefText(buildBrief())} onRecap={() => setShowRecap(true)}
        retCheck={retCheck} onReturnDesign={() => setRetOpen(true)} onLogs={() => setShowLogs(true)} />
      {retOpen && (
        <ReturnToDesignDialog item={{ id, address: p.address }} check={retCheck?.detail}
          onClose={() => setRetOpen(false)}
          onDone={async () => { setRetOpen(false); setRetCheck(null); setTick((t) => t + 1); await load(); window.dispatchEvent(new Event("kickoff-changed")); }} />
      )}
      {showRecap && <CommRecapDialog api={api} zh={zh} user={user} projectId={id} onClose={() => setShowRecap(false)} />}
      {/* AI 诊断后先查看现场快照，再进入阶段任务链及右侧项目讨论。
          工作台保持完整，阶段切换、任务操作及讨论仍共享原工作区状态。 */}
      <Expandable testId="hub-ai-card" zh={zh} title={zh ? "AI 工程管家" : "AI Assistant"}><AICard data={data} zh={zh} api={api} id={id} aiBusy={aiBusy} setData={setData} setAiBusy={setAiBusy} /></Expandable>
      {/* 现场快照位于全链路工作台之前；归档入口打开项目文件并定位到工地照片分类。 */}
      <section ref={photosSectionRef} tabIndex={-1} aria-label={zh ? "现场快照" : "Site photos"}
        className="scroll-mt-4 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
        <Expandable testId="hub-photos" zh={zh} title={zh ? "现场快照" : "Site photos"}><SitePhotosCard data={data} zh={zh} api={api} id={id} reload={load} showUpload={showUpload} setShowUpload={setShowUpload} onArchive={() => openFiles("job_site_photos")} /></Expandable>
      </section>
      <TraceWorkspace api={api} id={id} zh={zh} user={user} onChanged={bump} refreshTick={tick}
        projectActions={<>
          <Button type="button" data-testid="hub-files-btn" variant="outline" onClick={() => nav(subcontractUrl(id), { state: { subcontractReturn } })} className="min-h-11 h-auto whitespace-normal text-left">
            <UsersRound aria-hidden="true" />{zh ? "分包商管理" : "Subcontractors"}
          </Button>
          {/* 日程入口与服务端使用同一动作权限；关闭后由 Trigger 自动恢复键盘焦点。 */}
          {permOf("/board::schedule-manage") === true && (
            <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
              <DialogTrigger asChild>
                <Button type="button" data-testid="hub-schedule-toggle" variant="outline" className="min-h-11 h-auto whitespace-normal text-left">
                  <CalendarDays aria-hidden="true" />{zh ? "分包商施工日程" : "Subcontractor schedule"}
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="hub-schedule" className="max-w-[96vw] p-3 pt-14 sm:p-4 sm:pt-14" closeLabel={zh ? "关闭施工日程" : "Close construction schedule"}>
                {/* 日历自带可见标题和项目地址；保留无障碍弹窗名称，顶部给关闭按钮独立留位，避免遮住日历操作。 */}
                <DialogTitle className="sr-only">{zh ? "分包商施工日程" : "Subcontractor schedule"}</DialogTitle>
                <DialogDescription className="sr-only">{p.address}</DialogDescription>
                <div className="flex flex-wrap gap-2" aria-label={zh ? "分包合同操作" : "Subcontract actions"} data-testid="hub-schedule-subcontract-actions">
                  <Button type="button" size="sm" variant="outline" className="min-h-10 h-auto whitespace-normal" data-testid="hub-schedule-rfq" onClick={() => openSubcontractCreate("rfq")}><Send className="h-4 w-4" aria-hidden="true" />{zh ? "发起询价" : "Request quotes"}</Button>
                  <Button type="button" size="sm" variant="outline" className="min-h-10 h-auto whitespace-normal" data-testid="hub-schedule-direct-contract" onClick={() => openSubcontractCreate("direct")}><FilePlus2 className="h-4 w-4" aria-hidden="true" />{zh ? "直接建合同" : "Create contract"}</Button>
                  <Button type="button" size="sm" variant="outline" className="min-h-10 h-auto whitespace-normal" data-testid="hub-schedule-offline-contract" onClick={() => openSubcontractCreate("offline")}><FileUp className="h-4 w-4" aria-hidden="true" />{zh ? "补录线下合同" : "Record offline contract"}</Button>
                </div>
                <SubcontractorSchedule key={id} projectId={id} />
              </DialogContent>
            </Dialog>
          )}
        </>}
        headerExtra={<>
          <Button data-testid="hub-new-task-btn" size="sm" variant="outline" onClick={() => setShowNewTask(true)} className="h-8 text-xs"><Plus className="h-3.5 w-3.5 mr-1" />{zh ? "新建任务" : "New task"}</Button>
          {p.stage === "completed" && (
            <Button data-testid="hub-owner-survey-btn" size="sm" variant="outline" onClick={sendOwnerSurvey} className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"><Star className="h-3.5 w-3.5 mr-1" />{zh ? "业主评价" : "Owner survey"}</Button>
          )}
        </>}
        todaySlot={<TodayPanel data={data} zh={zh} api={api} id={id} user={user} reload={bump} />}
        sideSlot={<HubDiscussion api={api} id={id} zh={zh} user={user} tasks={data.tasks} phases={data.phases} />} />
      <DiscussionCard api={api} id={id} zh={zh} user={user} defaultOpen={focusSection === "discussion"} />
      {/* 二-12：「工期变更」板块已从项目详情页隐藏。
          老板原话：工期变更项目里自己填完后就已有，没必要在 Hub 再单独显示一遍。
          相关能力仍在 DateChangeCard 组件中，如需恢复可取消下一行注释。 */}
      {/* <DateChangeCard api={api} id={id} zh={zh} user={user} onChanged={bump} refreshTick={tick} /> */}
      {/* 按项目详情精简需求移除整条全链路摘要（阶段、负责人、催办与巡检入口），不保留占位。
          阶段任务继续由上方工作台承载，巡检仍可从现有巡检总览进入。 */}
      <HandoffReceipt projectId={id} zh={zh} />
      {/* 工程经理/项目经理可在原项目发起改图需求；设计经理统一指派。 */}
      {["admin", "ceo", "eng_manager", "pm", "cdc", "design_manager"].includes(user?.role) && <DesignChanges engineeringProjectId={id} compact />}
      <SubRatingPanel api={api} id={id} zh={zh} refreshTick={tick} focusId={rateFocus} />
      <RfqComparePanel api={api} id={id} zh={zh} refreshTick={tick} />
      {briefText && <BriefModal text={briefText} zh={zh} api={api} id={id} user={user} ownerLang={p.ownerLang || "zh"} onClose={() => setBriefText(null)} />}
      <EditProject project={p} open={editOpen} onClose={() => { setEditOpen(false); bump(); }}
        footerActions={<ProjectManagementActions api={api} project={p} zh={zh} user={user} onDeleted={() => { if (embedded && onClose) onClose(); else nav("/board"); }} />} />
      {showFiles && (
        <FloatWindow testId="hub-projfiles" zh={zh} title={zh ? "项目文件" : "Project Files"} onClose={() => { setShowFiles(false); setFilesFocus(""); }}>
          <div className="bg-white rounded-xl p-3"><ProjectFiles key={`${id}-${filesFocus || "all"}`} projectId={id} focusCategory={filesFocus} /></div>
        </FloatWindow>
      )}
      {showLogs && (
        <FloatWindow testId="hub-dailylogs" zh={zh} title={zh ? "施工日志" : "Daily Logs"} onClose={() => setShowLogs(false)}>
          <div className="bg-white rounded-xl p-3" data-testid="hub-dailylogs-body"><ProjectDailyLogs projectId={id} /></div>
        </FloatWindow>
      )}
      {showNewTask && <NewTaskModal zh={zh} api={api} id={id} data={data} onClose={() => setShowNewTask(false)} reload={load} />}
    </div>
  );
}

// 项目详情页头只保留现场常用操作；二维码、门户和删除统一由编辑弹窗底部承接。
function HubHeader({ headerRef, p, zh, nav, api, id, user, embedded = false, onClose, onFiles, onPhoto, onBrief, onEdit, onRecap, retCheck, onReturnDesign, onLogs }) {
  const staff = useStaffOptions();
  const br = (staff.branches || []).find((b) => b.id === p.branch);
  const [mapOpen, setMapOpen] = useState(false);
  // 已退回设计部的项目从工程项目清单隐藏；若用户仍通过历史链接打开详情页，继续展示退回状态。
  // 兼容标准流程（kickoff.status=returned）和人工退回（paused + pauseSource=eng_return_design）。
  // 刻意不依赖 retCheck 做主判定：retCheck 仅 admin/ceo/eng_manager 拉取（接口 403 兜底），
  // 其余工程侧角色（PM / 协办等）用项目自身字段兜底；retCheck 命中时优先用它的字段（老数据可能缺 paused）。
  const returnedInfo = retCheck?.reason === "already_returned"
    ? retCheck
    : (p.paused && p.pauseSource === "eng_return_design"
      ? { returnedAt: p.pausedAt, returnedBy: p.pausedBy, returnReason: (p.pauseReason || "").replace(/^退回设计部[:：]/, "") }
      : null);
  // 手机先展示现场最常用的三个操作；其它入口仍保留原权限与回调，展开后可访问。
  // 只调整展示层，回执等动作仍使用原接口，电脑继续显示并列工具栏。
  const [moreActions, setMoreActions] = useState(false);
  useEffect(() => { setMoreActions(false); }, [id]);
  return (
    // 三-9：缩小项目头部信息块。老板原话（25:32-26:14）：这些板块没必要放那么大，
    // 占这么大位置却没放很多东西，给管理层看也可以做成小一些、紧凑排列。
    // 改动：内边距 p-4→p-3，地址字号 text-lg→text-base，人员信息行 mt-1→mt-0.5，标签内边距缩小。
    // 下滑查看任务时仍需辨认当前项目并使用现场操作，因此将原信息卡整体吸顶，保留自然占位避免内容跳动。
    // 抽屉以自身滚动区顶部为基准；整页沿用客户详情的面包屑下方吸附位，层级低于弹窗与全局导航。
    <div ref={headerRef} className={`sticky z-20 ${embedded ? "top-0" : "below-breadcrumb"} bg-white rounded-xl border border-slate-200 p-3 shadow-sm`} data-testid="hub-header">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* 返回：整页态回团队看板（原行为）；抽屉态关抽屉回来源页 */}
            <button type="button" data-testid="hub-back" aria-label={zh ? "返回项目列表" : "Back to projects"} onClick={() => (embedded && onClose ? onClose() : nav("/board"))} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 md:h-auto md:w-auto"><ArrowLeft className="h-4 w-4" /></button>
            {/* 地址可点击：左键/右键（浏览器菜单「在新标签页打开链接」）直达 Google 地图查看该场地；
                后方地图钉按钮打开内置地图弹窗（无经纬度时按地址 geocode）。 */}
            <h1 className="min-w-0 flex-1 basis-[calc(100%_-_4rem)] break-words text-base sm:text-lg font-extrabold tracking-tight text-slate-900 [overflow-wrap:anywhere] md:flex-none md:basis-auto md:truncate" data-keep-title>
              <a href={gmapsSearchUrl(p)} target="_blank" rel="noreferrer" data-testid="hub-address-link"
                title={zh ? "点击（或右键 → 在新标签页打开）在 Google 地图中查看" : "Click (or right-click → open in new tab) to view in Google Maps"}
                className="hover:text-indigo-600 hover:underline decoration-indigo-300 underline-offset-4 transition-colors">{p.address}</a>
            </h1>
            <button type="button" data-testid="hub-address-map-btn" onClick={() => setMapOpen(true)} aria-label={zh ? "查看地图" : "View map"} title={zh ? "查看地图" : "View map"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors md:h-auto md:w-auto"><MapPin className="h-4 w-4" /></button>
            {canEditProject(user, p) && <button type="button" data-testid="hub-edit-project-btn" onClick={onEdit} aria-label={zh ? "编辑项目信息" : "Edit project"} title={zh ? "编辑项目信息" : "Edit project"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors md:h-auto md:w-auto"><Pencil className="h-3.5 w-3.5" /></button>}
            {p.type && <span className="text-[10px] font-bold px-1 py-px rounded bg-indigo-100 text-indigo-700">{p.type}</span>}
            {p.branch && <span className="text-[10px] font-bold px-1 py-px rounded bg-amber-50 text-amber-700" data-testid="hub-branch-tag" title={zh ? "所属分公司" : "Branch"}>{br ? (zh ? br.zh : br.en) : p.branch}</span>}
            {(p.teamName || p.team) && <span className="text-[10px] font-bold px-1 py-px rounded bg-slate-100 text-slate-600" data-testid="hub-team-tag">{p.teamName || p.team}</span>}
            {/* VR 分体按钮（左主按钮 + 右下拉）：有链接直接新标签页打开最新一条；无链接弹填写框；
                下拉列全部链接 + 底部常驻「添加视频链接」。数据源 engine_vr 集合。 */}
            <VrLinkButton api={api} id={id} zh={zh} user={user} />
            {/* 项目组需要在顶部及时看到本周市检，故放在 VR 与现场拍照之间；无预约不占位。 */}
            <WeeklyInspectionSummary key={id} api={api} projectId={id} zh={zh} />
          </div>
          <p className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {/* 页头不再显示业主姓名与语言；隐藏节点保留原 testid，字段仍可在编辑表单中维护。 */}
            <span hidden><User className="h-3 w-3 inline mr-0.5" />{zh ? "业主" : "Owner"} {p.clientName || "-"}<span className="ml-1 text-[9px] font-bold px-1 py-px rounded bg-emerald-50 text-emerald-700" data-testid="hub-owner-lang-tag" title={zh ? "业主首选语言" : "Owner language"}>{p.ownerLang === "en" ? "EN" : p.ownerLang === "es" ? "ES" : "中文"}</span></span>
            <span data-testid="hub-manager">{zh ? "场地 PM" : "Field PM"} {p.manager || "-"}{p.coStaff?.manager?.length > 0 && <span className="text-indigo-500" data-testid="hub-co-manager"> +{zh ? "协办" : "co"} {p.coStaff.manager.join("、")}</span>}</span>
            {p.onlineManager && <span data-testid="hub-online-manager">{zh ? "线上 PM" : "Online PM"} {p.onlineManager}{p.coStaff?.onlineManager?.length > 0 && <span className="text-indigo-500" data-testid="hub-co-online-manager"> +{zh ? "协办" : "co"} {p.coStaff.onlineManager.join("、")}</span>}</span>}
            {p.designer && <span data-testid="hub-designer">{zh ? "建筑设计" : "Architect"} {p.designer}{p.coStaff?.designer?.length > 0 && <span className="text-indigo-500" data-testid="hub-co-designer"> +{zh ? "协办" : "co"} {p.coStaff.designer.join("、")}</span>}</span>}
            {p.drafter && <span data-testid="hub-drafter">{zh ? "施工图" : "Drafter"} {p.drafter}</span>}
            <span>{zh ? "销售" : "Sales"} {p.sales || "-"}</span>
            <span>{zh ? "开工" : "Start"} {fmtD(p.startDate || p.expectedStart, "-")}</span>
          </p>
        </div>
        <div className="flex w-full items-center gap-2 flex-wrap md:w-auto">
          {/* 手机主操作不需要展开更多，也不会随更低频的管理按钮挤成多行小字。 */}
          <div className="grid w-full grid-cols-3 gap-2 md:contents">
            <Button data-testid="hub-photo-btn" size="sm" variant="outline" onClick={onPhoto} className="h-auto min-h-14 flex-col gap-1 whitespace-normal px-1 text-xs md:h-8 md:min-h-0 md:flex-row md:gap-0 md:px-3"><Camera className="h-4 w-4 shrink-0 md:mr-1 md:h-3.5 md:w-3.5" />{zh ? "现场拍照" : "Photos"}</Button>
            <Button data-testid="hub-project-files-btn" size="sm" variant="outline" onClick={onFiles} className="h-auto min-h-14 flex-col gap-1 whitespace-normal px-1 text-xs md:h-8 md:min-h-0 md:flex-row md:gap-0 md:px-3"><FolderOpen className="h-4 w-4 shrink-0 md:mr-1 md:h-3.5 md:w-3.5" />{zh ? "项目文件" : "Files"}</Button>
            <Button data-testid="hub-dailylogs-btn" size="sm" variant="outline" onClick={onLogs} className="h-auto min-h-14 flex-col gap-1 whitespace-normal px-1 text-xs md:h-8 md:min-h-0 md:flex-row md:gap-0 md:px-3"><ClipboardList className="h-4 w-4 shrink-0 md:mr-1 md:h-3.5 md:w-3.5" />{zh ? "施工日志" : "Daily Logs"}</Button>
          </div>
          <button type="button" data-testid="hub-more-actions" aria-expanded={moreActions} aria-controls={`hub-secondary-actions-${id}`} onClick={() => setMoreActions(open => !open)} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 md:hidden">{moreActions ? (zh ? "收起更多操作" : "Hide more actions") : (zh ? "更多操作" : "More actions")}<ChevronDown className={`h-4 w-4 ${moreActions ? "rotate-180" : ""}`} aria-hidden="true" /></button>
          <div id={`hub-secondary-actions-${id}`} className={`${moreActions ? "flex" : "hidden"} w-full flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3 [&_button]:min-h-11 md:contents md:[&_button]:min-h-0`}>
          <ContactDrop api={api} id={id} zh={zh} user={user} />
          <Button data-testid="hub-comm-recap-btn" size="sm" variant="outline" onClick={onRecap} className="h-8 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"><MailCheck className="h-3.5 w-3.5 mr-1" />{zh ? "通知回执" : "Notifications"}</Button>
          <Button data-testid="hub-brief-btn" size="sm" variant="outline" onClick={onBrief} className="h-8 text-xs"><Megaphone className="h-3.5 w-3.5 mr-1" />{zh ? "工程简报" : "Brief"}</Button>
          {/* 退回设计部：仅当服务端预检判定「尚未实质开工」时才出现（retCheck.ok），权限同后端 MGR_ROLES */}
          {retCheck?.ok && (
            <Button data-testid="hub-return-design-btn" size="sm" variant="outline" onClick={onReturnDesign}
              className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-600 hover:text-white">
              <RotateCcw className="h-3.5 w-3.5 mr-1" />{zh ? "退回设计部" : "Return to Design"}</Button>
          )}
          </div>
        </div>
        {/* 历史链接仍可打开已退回项目；详情页保留状态、退回人与原因，避免误认为项目已删除。 */}
        {returnedInfo && (
          <div data-testid="hub-returned-banner" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            <p className="font-bold" data-testid="hub-returned-title">
              {zh ? "已退回设计部 · 工程侧暂停中" : "Returned to Design · Paused"}
              <span className="font-normal text-amber-700">{returnedInfo.returnedAt ? ` · ${String(returnedInfo.returnedAt).slice(0, 10)}` : ""}{returnedInfo.returnedBy ? ` · ${returnedInfo.returnedBy}` : ""}</span>
            </p>
            {returnedInfo.returnReason && <p data-testid="hub-returned-reason" className="mt-0.5">{zh ? "退回原因" : "Reason"}：{returnedInfo.returnReason}</p>}
            <p className="mt-0.5 text-amber-700">{zh ? "设计部补办完成并重新移交后，本项目自动恢复开工（退回期间计入暂停，不计入阶段停留天数）。" : "Auto-resumes when Design re-handoffs; pause time is excluded from phase dwell."}</p>
          </div>
        )}
      </div>
      {mapOpen && <AddressMapModal open onClose={() => setMapOpen(false)} project={p} zh={zh} />}
    </div>
  );
}

function AICard({ data, zh, api, id, aiBusy, setData, setAiBusy }) {
  const d = data.aiDiag;
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  // （2026-10-16 老板二次要求）二-10 只缩了字号不够 —— 卡片仍占顶部 4/10 列且很高。
  // 桌面默认一行：只留标题 + 延误徽标 + 摘要，原因列表与赶工按钮收进展开；
  // 展开状态记 localStorage，老板展开过一次之后就保持展开，不用每次点。
  const [open, setOpen] = useState(() => { try { return localStorage.getItem("hub.ai.open") === "1"; } catch { return false; } });
  const toggleOpen = () => setOpen((v) => { const nv = !v; try { localStorage.setItem("hub.ai.open", nv ? "1" : "0"); } catch {} return nv; });
  const rediag = async () => {
    setAiBusy(true);
    try { const { data: r } = await api.post(`/projects/${id}/hub/ai-diagnose`, { force: true }); setData((prev) => ({ ...prev, aiDiag: r.result, aiDiagDay: todayStr() })); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 暂不可用" : "AI unavailable")); }
    setAiBusy(false);
  };
  const rushPlan = async () => {
    setPlanBusy(true);
    try { const { data: r } = await api.post(`/projects/${id}/hub/rush-plan`); setPlan(r.plan); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 暂不可用" : "AI unavailable")); }
    setPlanBusy(false);
  };
  // 二-10：AI 主动预警板块按老板要求弱化显示——
  // 原话：「可以把它往後移，或者說把這個尺寸改小一些」。原因：超期预警已自动显示，
  // AI 当前还不够智能、规划内容不靠谱，板块占位大但内容少，主要给管理层扫一眼即可。
  // 处理：先缩字号（104f3bef），再折叠成一行（仍占 4/10 格、底下留白被老板打回），
  // 桌面保持整行细横幅；手机原来把标题、指标与按钮都设为不收缩，挤出边框并吞掉摘要。
  // 手机将标题和摘要各放一行，指标允许自然换行，展开/刷新提供 44px 点击区；折叠记忆与诊断业务不变。
  return (
    <div className="bg-amber-50/70 rounded-xl border border-amber-200 p-2.5" data-testid="hub-ai-card">
      <div className="flex flex-wrap md:flex-nowrap items-center gap-2 min-w-0">
        <p className="w-full md:w-auto text-xs font-semibold text-amber-800 flex items-center gap-1.5 shrink-0"><Bot className="h-3.5 w-3.5" />{zh ? "AI 项目诊断 · 主动预警" : "AI Diagnosis"}</p>
        {d && <span data-testid="hub-ai-delay" className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${(d.delayDays || 0) > 0 ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>{(d.delayDays || 0) > 0 ? (zh ? `预估延误 ${d.delayDays} 天` : `+${d.delayDays}d`) : (zh ? "进度正常" : "On track")}</span>}
        {d && !open && <p className="order-last w-full flex-none break-words text-[11px] text-amber-900 font-medium min-w-0 md:order-none md:w-auto md:flex-1 md:truncate" data-testid="hub-ai-summary">{T(d.summary)}</p>}
        <div className={`flex items-center gap-2 shrink-0 ml-auto ${d && !open ? "md:ml-0" : ""}`}>
          {d && <span className="whitespace-nowrap text-[10px] text-amber-600">{zh ? "置信度" : "conf."} {d.confidence ?? "-"}%</span>}
          <button data-testid="hub-ai-toggle" onClick={toggleOpen}
            aria-expanded={open} aria-label={open ? (zh ? "收起诊断详情" : "Collapse diagnosis") : (zh ? "展开诊断详情" : "Expand diagnosis")}
            className="flex min-h-11 min-w-11 md:min-h-0 md:min-w-0 items-center justify-center gap-0.5 text-[10px] font-semibold text-amber-700 hover:text-amber-900"
            title={open ? (zh ? "收起" : "Collapse") : (zh ? "展开诊断详情" : "Expand")}>
            {!open && (d?.causes || []).length > 0 && <span>{(d.causes || []).length}</span>}
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button data-testid="hub-ai-rediagnose" onClick={rediag} disabled={aiBusy} aria-label={zh ? "重新诊断" : "Re-run diagnosis"} className="flex min-h-11 min-w-11 md:min-h-0 md:min-w-0 items-center justify-center text-amber-600 hover:text-amber-800" title={zh ? "重新诊断" : "Re-run"}>
            {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {!d && <p className="text-[11px] text-amber-600 py-3 text-center">{aiBusy ? (zh ? "AI 正在分析引擎判定与滞后数据…" : "Analyzing…") : (zh ? "暂无诊断" : "No diagnosis")}</p>}
      {d && open && (
        <>
          <p className="text-[11px] text-amber-900 font-medium mt-1.5 mb-1" data-testid="hub-ai-summary">{T(d.summary)}</p>
          <div className="space-y-1">
            {(d.causes || []).map((c, i) => (
              <div key={i} className="bg-white/80 rounded-lg px-2 py-1 text-[11px] flex items-start justify-between gap-2" data-testid={`hub-ai-cause-${i}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{"①②③④⑤"[i] || i + 1} {T(c.title)}{c.delayDays ? <span className="text-red-600 ml-1">→ {zh ? `预估延误 ${c.delayDays} 天` : `+${c.delayDays}d`}</span> : null}</p>
                  <p className="text-slate-500 mt-0.5">{T(c.detail)}</p>
                </div>
                {c.action && <span className="shrink-0 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{T(c.action)}</span>}
              </div>
            ))}
            {(d.causes || []).length === 0 && <p className="text-[11px] text-emerald-700">{zh ? "未发现明显进度风险 ✓" : "No major risks ✓"}</p>}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button data-testid="hub-rush-plan-btn" size="sm" onClick={rushPlan} disabled={planBusy} className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700 text-white">{planBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Rocket className="h-3 w-3 mr-1" />}{zh ? "生成赶工计划" : "Rush plan"}</Button>
          </div>
        </>
      )}
      {plan && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPlan(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="hub-rush-plan-modal">
            <p className="font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Rocket className="h-4 w-4 text-amber-600" />{zh ? "AI 赶工计划" : "Rush plan"}</p>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">{plan}</pre>
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => { try { await navigator.clipboard.writeText(plan); toast.success(zh ? "已复制" : "Copied"); } catch { toast.error(zh ? "复制失败，请手动复制" : "Copy failed"); } }}>{zh ? "复制" : "Copy"}</Button>
              <Button data-testid="hub-rush-plan-close" size="sm" className="h-7 text-xs" onClick={() => setPlan(null)}>{zh ? "关闭" : "Close"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function stateChips(t, zh) {
  const out = [];
  const today = todayStr();
  if (t.state === "blocked") out.push([zh ? "被阻塞" : "blocked", "bg-red-100 text-red-700"]);
  if (t.state === "waiting_time") out.push([`${zh ? "等待时间" : "waiting"}${t.dueDate ? ` · ${String(t.dueDate).slice(5)}${zh ? "解锁" : " unlocks"}` : ""}`, "bg-amber-100 text-amber-700"]);
  if (t.state === "waiting_external") out.push([zh ? "等待外部" : "ext.", "bg-blue-100 text-blue-700"]);
  if (t.state === "ready" && (t.evidence || []).length === 0 && (t.reasons || []).some((r) => r.includes("证据"))) out.push([zh ? "需证据" : "evidence", "bg-amber-100 text-amber-700"]);
  if (t.custom && t.dueDate && String(t.dueDate) < today) {
    const od = Math.floor((new Date(today) - new Date(String(t.dueDate).slice(0, 10))) / 86400000);
    out.push([`${zh ? "已逾期" : "overdue"} ${od}${zh ? "天" : "d"}`, "bg-red-600 text-white"]);
  }
  if (t.source === "survey_risk") out.push([zh ? "踏勘风险" : "survey risk", "bg-red-100 text-red-700"]);
  else if (t.custom) out.push([zh ? "自定义" : "custom", "bg-slate-100 text-slate-500"]);
  return out;
}

function TodayPanel({ data, zh, api, id, user, reload }) {
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [view, setView] = useState(() => { try { return localStorage.getItem("hub_today_view") || "list"; } catch { return "list"; } });
  const setViewP = (v) => { setView(v); try { localStorage.setItem("hub_today_view", v); } catch {} };
  const me = user?.username;
  const list = useMemo(() => {
    const l = (data.tasks || []).filter((t) => {
      if (!t.today || t.status === "done" || t.archived) return false;
      if (phaseFilter !== "all" && t.phase !== Number(phaseFilter)) return false;
      if (mineOnly && t.assigneeUsername !== me) return false;
      return true;
    });
    l.sort((a, b) => ((a.assigneeUsername === me ? 0 : 1) - (b.assigneeUsername === me ? 0 : 1)) || (a.phase - b.phase));
    return l;
  }, [data.tasks, phaseFilter, mineOnly, me]);
  const groups = useMemo(() => {
    const m = {};
    list.forEach((t) => { const k = t.role || "crew"; (m[k] = m[k] || []).push(t); });
    return Object.entries(m);
  }, [list]);
  const [doneIds, setDoneIds] = useState([]);
  const [upBusyId, setUpBusyId] = useState(null);
  const [previewEv, setPreviewEv] = useState(null);
  const [noteTask, setNoteTask] = useState(null);
  const [showDone, setShowDone] = useState(true);
  const upTaskRef = useRef(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const doneToday = useMemo(() => (data.tasks || []).filter((t) => t.status === "done" && !t.archived && String(t.doneAt || "").slice(0, 10) === todayStr()), [data.tasks]);
  const finishAnim = (t) => {
    setDoneIds((d) => [...d, t.id]);
    const remaining = list.filter((x) => x.id !== t.id && !doneIds.includes(x.id)).length;
    if (remaining === 0) {
      confetti({ particleCount: 180, spread: 80, origin: { y: 0.7 } });
      setTimeout(() => confetti({ particleCount: 90, spread: 120, origin: { y: 0.5 } }), 350);
      toast.success(zh ? "🎉 今日必办全部完成！" : "🎉 All done for today!");
    }
    setTimeout(() => reload(), 900);
  };
  const act = async (t, action, body) => {
    try {
      const { data: r } = await api.post(`/projects/${id}/engine/tasks/${t.id}/${action}`, body || {});
      if (action === "nudge") toast.success(zh ? `已催办（通知 ${r.notified?.length || 0} 人）` : "Nudged");
      if (r?.advanced) toast.success(zh ? `🚀 阶段门槛全部满足，已自动推进至「${r.advanced.toName}」` : `Advanced to ${r.advanced.toName}`, { duration: 6000 });
      if (action === "complete") { toast.success(zh ? "任务已完成 ✓" : "Done"); finishAnim(t); return; }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const pickUpload = (t, cam) => { upTaskRef.current = t; (cam ? camRef : fileRef).current?.click(); };
  const uploadComplete = async (files) => {
    const t = upTaskRef.current;
    if (!t || !files.length) return;
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) { toast.error(zh ? `「${f.name}」超过 25MB 上限` : "Max 25MB per file"); return; }
    }
    setUpBusyId(t.id);
    const progId = files.length > 1 ? toast.loading(zh ? `上传凭证 1/${files.length}…` : `Uploading 1/${files.length}…`) : null;
    try {
      for (let i = 0; i < files.length; i++) {
        if (progId) toast.loading(zh ? `上传凭证 ${i + 1}/${files.length}：${files[i].name}` : `Uploading ${i + 1}/${files.length}`, { id: progId });
        const up = await chunkUpload(api, files[i]);
        await api.post(`/projects/${id}/engine/tasks/${t.id}/evidence`, up);
      }
      if (progId) toast.dismiss(progId);
      const names = files.map((f) => f.name).join("、").slice(0, 260);
      const { data: r } = await api.post(`/projects/${id}/engine/tasks/${t.id}/complete`, { note: T("上传凭证完成：{0}", [names]) });
      toast.success(zh ? `${files.length} 份凭证已归档到项目证据，任务自动完成 ✓` : "Uploaded & completed ✓");
      if (r?.advanced) toast.success(zh ? `🚀 已自动推进至「${r.advanced.toName}」` : `Advanced to ${r.advanced.toName}`, { duration: 6000 });
      finishAnim(t);
    } catch (e) {
      if (progId) toast.dismiss(progId);
      toast.error(e?.response?.data?.detail || (zh ? "上传或完成失败" : "Upload/complete failed"));
      reload();
    }
    setUpBusyId(null);
  };
  const exportCsv = () => {
    const rows = [zh ? ["任务", T("阶段"), T("子阶段"), T("角色"), T("判定"), T("截止")] : ["Task", "Phase", "Sub-phase", "Role", "State", "Due"], ...list.map((t) => [T(t.title), T(data.phases[t.phase] || ""), T(t.subName || ""), T(data.roles[t.role] || t.role), t.state, t.dueDate || ""])];
    const csv = "\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `today_tasks_${id}.csv`;
    a.click();
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 h-full" data-testid="hub-today-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><ClipboardList className="h-4 w-4 text-red-500" />{zh ? "今日必办" : "Today"}<span className="text-[11px] font-normal text-slate-400">{list.length}{zh ? " 项" : ""}</span></p>
        <div className="flex items-center gap-2 text-[11px]">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="hub-today-view-toggle">
            <button data-testid="hub-today-view-list" onClick={() => setViewP("list")} title={zh ? "列表显示" : "List"}
              className={`px-2 py-1 flex items-center gap-1 ${view === "list" ? "bg-slate-800 text-white" : "bg-white text-slate-400 hover:text-slate-600"}`}>
              <List className="h-3.5 w-3.5" />{zh ? "列表" : "List"}</button>
            <button data-testid="hub-today-view-cards" onClick={() => setViewP("cards")} title={zh ? "任务卡显示" : "Cards"}
              className={`px-2 py-1 flex items-center gap-1 ${view === "cards" ? "bg-slate-800 text-white" : "bg-white text-slate-400 hover:text-slate-600"}`}>
              <LayoutGrid className="h-3.5 w-3.5" />{zh ? "任务卡" : "Cards"}</button>
          </div>
          <label className="flex items-center gap-1 cursor-pointer text-slate-500" data-testid="hub-today-mine-toggle">
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} className="accent-indigo-600" />{zh ? "仅我的" : "Mine"}
          </label>
          <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} className="rounded border border-slate-200 px-1.5 py-0.5 bg-white" data-testid="hub-today-phase-filter">
            <option value="all">{zh ? "全部阶段" : "All"}</option>
            {(data.phases || []).map((n, x) => <option key={x} value={x}>{T(n)}</option>)}
          </select>
          <button data-testid="hub-today-export" onClick={exportCsv} className="text-slate-400 hover:text-slate-600" title={zh ? "导出" : "Export"}><Download className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {groups.length === 0 && <p className="text-xs text-slate-300 text-center py-8" data-testid="hub-today-empty">{zh ? "今日无必办任务 🎉" : "Nothing due today"}</p>}
      {view === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 max-h-[560px] overflow-y-auto pr-1" data-testid="hub-today-cards">
          {list.map((t) => (
            <div key={t.id} className={`rounded-xl border border-slate-200 bg-slate-50/70 hover:bg-slate-50 p-3 flex flex-col gap-1.5 transition-all ${doneIds.includes(t.id) ? "opacity-50" : ""}`} data-testid={`hub-today-card-${t.id}`}>
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ROLE_COLOR[t.role] || "bg-slate-100 text-slate-600"}`}>{T(data.roles[t.role] || t.role)}</span>
                {t.assigneeUsername === user?.username && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{zh ? "指派给我" : "mine"}</span>}
                <span className="ml-auto text-[10px] text-slate-400">{t.dueDate ? String(t.dueDate).slice(5) : ""}</span>
              </div>
              <p className={`text-xs font-semibold leading-snug transition-all ${doneIds.includes(t.id) ? "line-through text-emerald-600" : "text-slate-800"}`} title={T(t.duty || t.title)}>{T(t.title)}</p>
              <p className="text-[10px] text-slate-400 truncate">{T(t.subName || data.phases[t.phase])}{t.assignee ? ` · ${t.assignee}` : ""}</p>
              <div className="flex flex-wrap gap-1">{stateChips(t, zh).map(([l, c], x) => <span key={x} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${c}`}>{l}</span>)}</div>
              {t.state === "blocked" && (t.reasons || []).length > 0 && <p className="text-[10px] text-red-600 truncate">{t.reasons.map((r) => T(r)).join(zh ? "；" : "; ")}</p>}
              <div className="flex gap-1.5 mt-auto pt-1">
                {t.state !== "blocked" && <button data-testid={`hub-card-done-${t.id}`} onClick={() => setNoteTask(t)} className="flex-1 text-[10px] font-semibold rounded-md px-1.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "完成" : "Done"}</button>}
                <button data-testid={`hub-card-upload-${t.id}`} disabled={upBusyId === t.id} onClick={() => pickUpload(t)} title={zh ? "上传照片/文件凭证，归档后自动完成" : "Upload proof to complete"}
                  className="flex-1 text-[10px] font-semibold rounded-md px-1.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center justify-center gap-1">
                  {upBusyId === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{zh ? "传凭证" : "Upload"}</button>
                <button data-testid={`hub-card-camera-${t.id}`} disabled={upBusyId === t.id} onClick={() => pickUpload(t, true)} title={zh ? "手机直接拍照上传，即传即完成" : "Take photo to complete"}
                  className="text-[10px] font-semibold rounded-md px-1.5 py-1 bg-slate-800 text-white hover:bg-slate-700 inline-flex items-center justify-center"><Camera className="h-3 w-3" /></button>
                <button data-testid={`hub-card-nudge-${t.id}`} onClick={() => act(t, "nudge")} className="flex-1 text-[10px] font-semibold rounded-md px-1.5 py-1 bg-amber-100 text-amber-700 hover:bg-amber-200">{zh ? "催办" : "Nudge"}</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
        {groups.map(([role, ts]) => (
          <div key={role} data-testid={`hub-today-group-${role}`}>
            <p className="text-[11px] font-bold text-slate-500 mb-1">
              <span className={`px-1.5 py-0.5 rounded ${ROLE_COLOR[role] || "bg-slate-100 text-slate-600"}`}>{T(data.roles[role] || role)}</span>
              <span className="ml-1.5 text-slate-400 font-normal">{[...new Set(ts.map((t) => t.assignee).filter(Boolean))].join(" · ")} {ts.length}{zh ? "项" : ""}</span>
            </p>
            {ts.map((t) => (
              <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-slate-50" data-testid={`hub-today-task-${t.id}`}>
                <input type="checkbox" checked={doneIds.includes(t.id)} onChange={() => setNoteTask(t)} className="accent-emerald-600 shrink-0" data-testid={`hub-today-check-${t.id}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium truncate transition-all ${doneIds.includes(t.id) ? "line-through text-emerald-600" : "text-slate-700"}`} title={T(t.duty || t.title)}>{T(t.title)}</p>
                  <p className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className="text-[10px] text-slate-400">{T(t.subName || data.phases[t.phase])}{t.dueDate ? ` · ${String(t.dueDate).slice(5)}` : ""}{t.assignee ? ` · ${t.assignee}` : ""}</span>
                    {t.assigneeUsername === user?.username && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{zh ? "指派给我" : "mine"}</span>}
                    {stateChips(t, zh).map(([l, c], x) => <span key={x} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${c}`}>{l}</span>)}
                  </p>
                  {t.state === "blocked" && (t.reasons || []).length > 0 && <p className="text-[10px] text-red-600 mt-0.5 truncate">{t.reasons.map((r) => T(r)).join(zh ? "；" : "; ")}</p>}
                </div>
                <span className="flex items-center gap-1 shrink-0">
                  {t.state !== "blocked" && <button data-testid={`hub-task-done-${t.id}`} onClick={() => setNoteTask(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "完成" : "Done"}</button>}
                  <button data-testid={`hub-task-upload-${t.id}`} disabled={upBusyId === t.id} onClick={() => pickUpload(t)} title={zh ? "上传照片/文件凭证，归档后自动完成" : "Upload proof to complete"}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-0.5">
                    {upBusyId === t.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Upload className="h-2.5 w-2.5" />}{zh ? "传凭证" : "Upload"}</button>
                  <button data-testid={`hub-task-camera-${t.id}`} disabled={upBusyId === t.id} onClick={() => pickUpload(t, true)} title={zh ? "手机直接拍照上传，即传即完成" : "Take photo to complete"}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-800 text-white hover:bg-slate-700 inline-flex items-center"><Camera className="h-2.5 w-2.5" /></button>
                  <button data-testid={`hub-task-nudge-${t.id}`} onClick={() => act(t, "nudge")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-700 hover:bg-amber-200">{zh ? "催办" : "Nudge"}</button>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      )}
      {doneToday.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2" data-testid="hub-today-done-section">
          <button data-testid="hub-today-done-toggle" onClick={() => setShowDone(!showDone)}
            className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
            ✓ {zh ? `今日已完成 ${doneToday.length} 项（点凭证可直接查看）` : `Done today ${doneToday.length}`}
            <ChevronDown className={`h-3 w-3 transition-transform ${showDone ? "rotate-180" : ""}`} />
          </button>
          {showDone && (
            <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto pr-1">
              {doneToday.map((t) => (
                <div key={t.id} className="flex items-center gap-2 py-1 border-b border-slate-50" data-testid={`hub-today-done-${t.id}`}>
                  <span className="text-emerald-500 text-xs shrink-0">✓</span>
                  <p className="text-xs text-slate-400 line-through truncate flex-1 min-w-0" title={T(t.title)}>{T(t.title)}</p>
                  <span className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                    {(t.evidence || []).slice(0, 4).map((e, x) => (
                      <button key={x} data-testid={`hub-today-ev-${t.id}-${x}`} onClick={() => setPreviewEv(e)} title={e.filename}
                        className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 inline-flex items-center gap-0.5">
                        {(e.contentType || "").startsWith("image") ? <ImageIcon className="h-2.5 w-2.5" /> : <Paperclip className="h-2.5 w-2.5" />}
                        {zh ? "凭证" : "file"}{(t.evidence || []).length > 1 ? x + 1 : ""}</button>
                    ))}
                    <span className="text-[10px] text-slate-300">{t.doneBy || ""}{t.doneAt ? ` ${String(t.doneAt).slice(11, 16)}` : ""}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" data-testid="hub-today-upload-input"
        onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) uploadComplete(fs); }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="hub-today-camera-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadComplete([f]); }} />
      <EvidenceLightbox ev={previewEv} zh={zh} onClose={() => setPreviewEv(null)} />
      {noteTask && <CompleteNoteModal title={T(noteTask.title)} zh={zh} api={api}
        onConfirm={async (note, audio) => {
          await act(noteTask, "complete", { ...(note ? { note } : {}), ...(audio ? { audioFileId: audio.fileId, durationSec: audio.durationSec } : {}) });
          setNoteTask(null);
        }}
        onClose={() => setNoteTask(null)} />}
    </div>
  );
}

function NewTaskModal({ zh, api, id, data, onClose, reload }) {
  const pi = Math.min(Math.max(data.phaseIdx ?? 0, 0), 7);
  const [f, setF] = useState({ title: "", phase: pi, role: "pm", assignee: "", dueDate: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error(zh ? "请输入任务标题" : "Title required"); return; }
    setBusy(true);
    try { await api.post(`/projects/${id}/engine/custom-task`, f); toast.success(zh ? "自定义任务已创建" : "Created"); onClose(); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="hub-newtask-modal">
        <p className="font-bold text-slate-800 mb-1">{zh ? "新建自定义任务" : "New custom task"}</p>
        <p className="text-[10px] text-slate-400 mb-3">{zh ? "自定义任务不影响引擎的门槛判定，用于记录项目额外工作" : "Custom tasks don't affect gate decisions"}</p>
        <input autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={zh ? "任务标题" : "Title"} className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 mb-2" data-testid="hub-newtask-title" />
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={f.phase} onChange={(e) => setF({ ...f, phase: Number(e.target.value) })} className="text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="hub-newtask-phase">
            {(data.phases || []).map((n, x) => <option key={x} value={x}>{T(n)}</option>)}
          </select>
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="hub-newtask-role">
            {Object.entries(data.roles || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} placeholder={zh ? "负责人" : "Assignee"} className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 mb-2" data-testid="hub-newtask-assignee" />
        <input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 mb-3" data-testid="hub-newtask-due" />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" className="h-7 text-xs bg-brand-500 hover:bg-brand-600 text-white" onClick={submit} disabled={busy} data-testid="hub-newtask-submit">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (zh ? "创建" : "Create")}</Button>
        </div>
      </div>
    </div>
  );
}

function Expandable({ children, testId, zh, title }) {
  const [full, setFull] = useState(false);
  return (
    <div className="relative h-full">
      <button data-testid={`${testId}-expand`} onClick={() => setFull(true)}
        title={zh ? "悬浮放大（可拖拽/调整大小）" : "Float window"}
        className="absolute -top-2 -right-2 z-20 h-6 w-6 rounded-full bg-white border border-slate-200 shadow-sm text-slate-400 hover:text-indigo-600 hover:border-indigo-300 flex items-center justify-center">
        <Maximize2 className="h-3 w-3" />
      </button>
      <div className="h-full">{children}</div>
      {full && (
        <FloatWindow testId={testId} zh={zh} title={title || (zh ? "悬浮视图" : "Float view")} onClose={() => setFull(false)}>
          {children}
        </FloatWindow>
      )}
    </div>
  );
}

function BriefModal({ text: localText, zh, api, id, user, ownerLang, onClose }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  useEffect(() => { api.get(`/projects/${id}/hub/brief-draft`).then(({ data }) => setDraft(data)).catch(() => setDraft({ text: localText, lang: zh ? "zh" : "en" })); }, [api, id, localText, zh]);
  const text = draft?.text || localText;
  const ownerLangName = { en: "English", es: "Español" }[draft?.ownerLang || ownerLang] || "中文";
  const [autoBusy, setAutoBusy] = useState(false);
  const canAuto = ["admin", "ceo", "eng_manager", "pm"].includes(user?.role);
  const runAuto = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post("/hub/brief-weekly-run", { projectId: id, force: true });
      toast.success(zh ? `已模拟周五自动推送（${data.week}）：${data.pushed} 个项目生成简报草稿 + 内部铃铛提醒` : `Pushed ${data.pushed} (${data.week})`, { duration: 6000 });
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setAutoBusy(false);
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + text], { type: "text/plain;charset=utf-8" }));
    a.download = zh ? `工程简报_${id}.txt` : `project_brief_${id}.txt`;
    a.click();
  };
  const sendAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/hub/brief-send`, { text });
      toast.success(zh ? `已生成 ${data.drafts} 条业主通信草稿（通信中心确认后发送）· 已站内通知 ${data.bells} 位相关成员` : `${data.drafts} drafts, ${data.bells} notified`, { duration: 6000 });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="hub-brief-modal">
        <p className="font-bold text-slate-800 mb-1 flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-indigo-600" />{zh ? "工程简报预览" : "Brief preview"}</p>
        <p className="text-[10px] text-slate-400 mb-2">{zh ? "「一键发送」将为业主生成短信/邮件草稿（通信中心确认后发送），并向经理/助理/顾问发送站内提醒。" : "“Send to all” drafts owner SMS/e-mail (confirm in Comm Center) and notifies PM / assistant / consultant in-app."}</p>
        <p className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1 mb-2 inline-flex items-center gap-1" data-testid="hub-brief-owner-lang">
          {zh ? `业主首选语言：${ownerLangName} · 以下即业主将收到的内容` : `Owner language: ${ownerLangName} · preview below is what the owner receives`}
        </p>
        <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-xl p-3 max-h-[50vh] overflow-y-auto" data-testid="hub-brief-preview" data-no-i18n>{text}</pre>
        <p className="text-[10px] text-indigo-500 bg-indigo-50 rounded-lg px-2.5 py-1.5 mt-2 flex items-center gap-1" data-testid="hub-brief-auto-note">
          ⏰ {zh ? "每周五 16:00 系统自动生成工程简报并推送给业主（通信草稿）及相关方（站内铃铛），无需手动操作" : "Auto-generated & pushed every Friday 4pm"}
        </p>
        <div className="flex justify-end gap-2 mt-4 flex-wrap">
          {canAuto && (
            <Button size="sm" variant="outline" disabled={autoBusy} onClick={runAuto} className="h-8 text-xs mr-auto" data-testid="hub-brief-auto-run">
              {autoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "模拟本周自动推送" : "Simulate weekly push")}</Button>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="hub-brief-copy"
            onClick={async () => { try { await navigator.clipboard.writeText(text); toast.success(zh ? "已复制" : "Copied"); } catch { toast.error(zh ? "复制失败" : "Copy failed"); } }}>{zh ? "复制" : "Copy"}</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={download} data-testid="hub-brief-download"><Download className="h-3.5 w-3.5 mr-1" />{zh ? "下载" : "Download"}</Button>
          <Button size="sm" disabled={busy} onClick={sendAll} className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="hub-brief-sendall">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "一键发送相关各方" : "Send to all")}</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose} data-testid="hub-brief-close">{zh ? "关闭" : "Close"}</Button>
        </div>
      </div>
    </div>
  );
}
